import { Controller } from '@hotwired/stimulus'

// "How to connect" stepper on generic source-to-destination pages
// (IntegrationSetupComponent). Shows one step at a time and refills every
// [data-live] template ("{schema}.{table}", "{mode}"...) when a choice changes.
export default class extends Controller {
  static targets = ['tab', 'pane', 'column']
  static values = { vars: Object }

  connect() {
    this.state = { ...this.varsValue }
  }

  select(event) {
    this.show(this.tabTargets.indexOf(event.currentTarget))
  }

  go(event) {
    this.show(Number(event.currentTarget.dataset.step), true)
  }

  key(event) {
    const index = this.tabTargets.indexOf(event.currentTarget)
    const last = this.tabTargets.length - 1
    const next = {
      ArrowDown: index + 1,
      ArrowRight: index + 1,
      ArrowUp: index - 1,
      ArrowLeft: index - 1,
      Home: 0,
      End: last
    }[event.key]
    if (next === undefined) return

    event.preventDefault()
    const target = (next + last + 1) % (last + 1)
    this.show(target)
    this.tabTargets[target].focus()
  }

  show(index, scroll = false) {
    this.tabTargets.forEach((tab, i) => {
      const active = i === index
      tab.setAttribute('aria-selected', active)
      tab.tabIndex = active ? 0 : -1
      this.paneTargets[i].classList.toggle('is-off', !active)
      this.paneTargets[i].inert = !active
    })
    if (scroll && this.element.getBoundingClientRect().top < 0) {
      this.element.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }
  }

  change(event) {
    const input = event.target
    if (input.name === 'integration-setup-entity') {
      this.state.entity = input.value
      this.state.table = snake(input.value)
    } else if (this.columnTargets.includes(input)) {
      this.state.cols = this.columnsText()
    } else if (input.name === 'integration-setup-mode') {
      this.state.mode = input.value
      this.element
        .querySelectorAll('.integration-setup__mode-note')
        .forEach(note => (note.dataset.live = input.dataset.note))
    } else if (input.name === 'integration-setup-schedule') {
      this.state.schedule = input.value
    } else {
      return
    }
    this.refresh()
  }

  columnsText() {
    const checked = this.columnTargets.filter(input => input.checked)
    if (!checked.length) return 'All columns'

    const count = checked.reduce((sum, input) => sum + Number(input.value), 0)
    const groups = checked.length === 1 ? 'group' : 'groups'
    return `${count.toLocaleString('en-US')} columns from ${
      checked.length
    } ${groups}`
  }

  refresh() {
    this.element.querySelectorAll('[data-live]').forEach(element => {
      const value = fill(element.dataset.live, this.state)
      if (element.textContent === value) return

      element.textContent = value
      element.classList.remove('is-changed')
      void element.offsetWidth
      element.classList.add('is-changed')
    })
  }
}

const fill = (template, vars) =>
  template.replace(/\{(\w+)\}/g, (match, key) => vars[key] ?? match)

const snake = text =>
  String(text || 'report')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
